import React, { useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import DynamicFormRenderer from '@/components/forms/DynamicFormRenderer'
import { CytologyFormSchema } from '@/types/formSchema'
import { logger } from '@/utils/logger'

interface Patient {
  id: number
  patient_id: string
  full_name: string
  date_of_birth: string
  gender: string
  age?: number
  address?: string
  province?: string
}

interface Annotation {
  id: number
  slide_id: string
  annotation_data: any
  label?: string
  thumbnail_url?: string
  created_at: string
}

interface CytologyReportPanelProps {
  slideId: string
  annotations: Annotation[]
  editReportId?: string
  onClose: () => void
}

interface CytologyTemplateConfig {
  hospitalName: string
  departmentName: string
  headerText: string
  hospitalAddress: string
  logoUrl: string
  defaultConclusion: string
  defaultRecommendation: string
  defaultOrganisms: Record<string, boolean>
  defaultSquamousCells: Record<string, boolean>
  defaultGlandularCells: Record<string, boolean>
  defaultOtherFindings: Record<string, boolean>
  formSchema?: CytologyFormSchema
}

// Bethesda standard sections that should not appear in custom fields
const BETHESDA_SECTIONS = [
  'Thông tin bệnh nhân',
  'Vi sinh vật (Organisms)',
  'Tế bào vảy (Squamous cell)',
  'Tế bào tuyến (Glandular cell)',
  'Biến đổi tế bào khác (Other non neoplastic findings)',
  'Kết luận và khuyến cáo'
]

export const CytologyReportPanel: React.FC<CytologyReportPanelProps> = ({ slideId, annotations: _annotations, editReportId, onClose }) => {
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [patientInfo, setPatientInfo] = useState<Patient | null>(null)
  const [templateConfig, setTemplateConfig] = useState<CytologyTemplateConfig | null>(null)
  const [existingReportId, setExistingReportId] = useState<string | null>(null)

  // Dynamic form data and validation errors
  const [dynamicFormData, setDynamicFormData] = useState<Record<string, any>>({})
  const [dynamicFormErrors, setDynamicFormErrors] = useState<Record<string, string>>({})

  // Selected annotations for report images (unused - only snapshots are used now)
  const [_selectedImage1, _setSelectedImage1] = useState<Annotation | null>(null)
  const [_selectedImage2, _setSelectedImage2] = useState<Annotation | null>(null)

  // Snapshots
  const [snapshots, setSnapshots] = useState<any[]>([])
  const [selectedSnapshot1, setSelectedSnapshot1] = useState<any | null>(null)
  const [selectedSnapshot2, setSelectedSnapshot2] = useState<any | null>(null)

  const [formData, setFormData] = useState({
    patientName: '',
    age: 0,
    gender: 'Nữ',
    patientCode: '',
    address: '',
    province: '',
    department: '',
    treatingDoctor: '',
    clinicalDiagnosis: '',
    specimenType: 'Phết nhúng dịch',
    organisms: {
      trichomonas: false,
      candida: false,
      bacterialVaginosis: false,
      actinomyces: false,
      hsv: false,
      hpv: false
    },
    squamousCells: {
      ascus: false,
      asch: false,
      lsil: false,
      lsilHpv: false,
      hsil: false,
      squamousCarcinoma: false
    },
    glandularCells: {
      agc: false,
      ais: false,
      endocervicalAdenocarcinoma: false,
      endometrialAdenocarcinoma: false,
      adenocarcinomaNos: false,
      adenocarcinomaExtrauterine: false
    },
    otherFindings: {
      normalCells: true,
      inflammation: false,
      radiation: false,
      iud: false,
      atrophy: false,
      postHysterectomy: false,
      endometrialCells: false
    },
    conclusion: 'TẾ BÀO BIỂU MÔ CỔ TỬ CUNG - ÂM ĐẠO TRONG GIỚI HẠN BÌNH THƯỜNG (NEGATIVE FOR INTRAEPITHELIAL LESION OR MALIGNANCY)',
    recommendation: 'Kết hợp kết quả xét nghiệm HPV theo dõi theo khuyến cáo.',
    cytopathologist: ''
  })

  // Load template config from API - only once when component mounts
  const [templateLoaded, setTemplateLoaded] = useState(false)

  useEffect(() => {
    if (templateLoaded) return // Prevent reloading and overwriting user input

    const fetchTemplate = async () => {
      try {
        // Fetch default template from API
        const response = await fetch('/api/v1/pathology/cytology-templates/default/')
        if (response.ok) {
          const template = await response.json()
          const config = template.config

          logger.debug('📋 [CytologyReportPanel] Loaded template config:', config)
          logger.debug('📋 [CytologyReportPanel] FormSchema exists:', !!config.formSchema)
          if (config.formSchema) {
            logger.debug('📋 [CytologyReportPanel] FormSchema sections:', config.formSchema.sections?.length)
          }

          setTemplateConfig(config)

          // Apply defaults only if template has default values
          setFormData(prev => ({
            ...prev,
            department: config.departmentName || prev.department,
            // Only apply template defaults if they exist, otherwise keep initial values
            organisms: config.defaultOrganisms ? { ...prev.organisms, ...config.defaultOrganisms } : prev.organisms,
            squamousCells: config.defaultSquamousCells ? { ...prev.squamousCells, ...config.defaultSquamousCells } : prev.squamousCells,
            glandularCells: config.defaultGlandularCells ? { ...prev.glandularCells, ...config.defaultGlandularCells } : prev.glandularCells,
            otherFindings: config.defaultOtherFindings ? { ...prev.otherFindings, ...config.defaultOtherFindings } : prev.otherFindings,
            conclusion: config.defaultConclusion || prev.conclusion,
            recommendation: config.defaultRecommendation || prev.recommendation
          }))
        } else {
          logger.debug('No default template found, using local defaults')
        }
      } catch (error) {
        console.error('Error loading template from API:', error)
      } finally {
        setTemplateLoaded(true)
      }
    }

    fetchTemplate()
  }, [templateLoaded])

  // Load patient info
  useEffect(() => {
    const fetchSlideInfo = async () => {
      try {
        setIsLoading(true)
        logger.debug('🔍 [CytologyReportPanel] Fetching slide info for slideId:', slideId)

        const response = await fetch(`/api/v1/imaging/slides/${slideId}/`)
        if (!response.ok) {
          console.error('❌ Failed to fetch slide:', response.status)
          toast.error('Không thể tải thông tin slide')
          setIsLoading(false)
          return
        }

        const slideData = await response.json()
        logger.debug('📊 Slide data:', slideData)

        let patientId = null
        let patientData = null

        // Priority 1: Check if patient object is embedded
        if (slideData.patient && typeof slideData.patient === 'object') {
          logger.debug('✅ Found patient object in slide data')
          patientData = slideData.patient
        }
        // Priority 2: Check if patient_id exists
        else if (slideData.patient_id) {
          logger.debug('🔑 Found patient_id:', slideData.patient_id)
          patientId = slideData.patient_id
        }
        // Priority 3: Check if patient is a number (ID reference)
        else if (slideData.patient && typeof slideData.patient === 'number') {
          logger.debug('🔑 Found patient as number ID:', slideData.patient)
          patientId = slideData.patient
        }
        // Priority 4: Check via case
        else if (slideData.case) {
          logger.debug('📂 Fetching case data for case ID:', slideData.case)
          try {
            const caseResponse = await fetch(`/api/v1/pathology/cases/${slideData.case}/`)
            if (caseResponse.ok) {
              const caseData = await caseResponse.json()
              logger.debug('📦 Case data:', caseData)

              if (caseData.patient && typeof caseData.patient === 'object') {
                patientData = caseData.patient
              } else if (caseData.patient) {
                patientId = caseData.patient
              }
            }
          } catch (caseError) {
            console.error('❌ Failed to fetch case:', caseError)
          }
        }

        // If we have patient data object, use it directly
        if (patientData) {
          logger.debug('✅ Setting patient info from object:', patientData)
          setPatientInfo(patientData)

          // Pre-fill form
          setFormData(prev => ({
            ...prev,
            patientName: patientData.full_name || '',
            age: patientData.age || 0,
            gender: patientData.gender === 'F' ? 'Nữ' : patientData.gender === 'M' ? 'Nam' : 'Khác',
            patientCode: patientData.patient_id || '',
            address: patientData.address || '',
            province: patientData.province || ''
          }))
        }
        // Otherwise fetch by ID
        else if (patientId) {
          logger.debug('🔍 Fetching patient by ID:', patientId)
          try {
            const patientResponse = await fetch(`/api/v1/pathology/patients/${patientId}/`)
            if (patientResponse.ok) {
              const patient = await patientResponse.json()
              logger.debug('✅ Fetched patient:', patient)
              setPatientInfo(patient)

              // Pre-fill form
              setFormData(prev => ({
                ...prev,
                patientName: patient.full_name || '',
                age: patient.age || 0,
                gender: patient.gender === 'F' ? 'Nữ' : patient.gender === 'M' ? 'Nam' : 'Khác',
                patientCode: patient.patient_id || '',
                address: patient.address || '',
                province: patient.province || ''
              }))
            } else {
              console.error('❌ Failed to fetch patient:', patientResponse.status)
              toast.error('Không thể tải thông tin bệnh nhân')
            }
          } catch (patientError) {
            console.error('❌ Failed to fetch patient:', patientError)
            toast.error('Lỗi khi tải thông tin bệnh nhân')
          }
        } else {
          console.warn('⚠️ No patient information found for this slide')
          toast.error('Slide này chưa được gán cho bệnh nhân nào')
        }
      } catch (error) {
        console.error('❌ Failed to load slide info:', error)
        toast.error('Lỗi khi tải thông tin slide')
      } finally {
        setIsLoading(false)
      }
    }

    if (slideId) {
      fetchSlideInfo()
    }
  }, [slideId])

  // Fetch snapshots for the slide
  useEffect(() => {
    const fetchSnapshots = async () => {
      try {
        const response = await fetch(`/api/v1/pathology/snapshots/?slide_id=${slideId}`)
        if (response.ok) {
          const data = await response.json()
          setSnapshots(data.results || data) // Handle paginated or direct array response
          logger.debug('📸 Loaded snapshots:', data)
        } else {
          console.error('Failed to fetch snapshots:', response.status)
        }
      } catch (error) {
        console.error('Error fetching snapshots:', error)
      }
    }

    if (slideId) {
      fetchSnapshots()
    }
  }, [slideId])

  // Load existing report for editing
  useEffect(() => {
    const loadExistingReport = async () => {
      if (!editReportId) return

      try {
        const response = await fetch(`/api/v1/pathology/cytology-reports/${editReportId}/`)
        if (response.ok) {
          const report = await response.json()

          // Populate form with existing data
          setFormData({
            patientName: report.patient_name || '',
            age: report.age || 0,
            gender: report.gender || 'Nữ',
            patientCode: report.patient_code || '',
            address: report.address || '',
            province: report.province || '',
            department: report.department || '',
            treatingDoctor: report.treating_doctor || '',
            clinicalDiagnosis: report.clinical_diagnosis || '',
            specimenType: report.specimen_type || 'Phết nhúng dịch',
            organisms: report.organisms || {},
            squamousCells: report.squamous_cells || {},
            glandularCells: report.glandular_cells || {},
            otherFindings: report.other_findings || {},
            conclusion: report.conclusion || '',
            recommendation: report.recommendation || '',
            cytopathologist: report.cytopathologist || ''
          })

          setExistingReportId(editReportId)
          toast.success('Đã tải thông tin phiếu để chỉnh sửa')
        } else {
          toast.error('Không thể tải thông tin phiếu')
        }
      } catch (error) {
        console.error('Error loading existing report:', error)
        toast.error('Lỗi khi tải thông tin phiếu')
      }
    }

    loadExistingReport()
  }, [editReportId])

  // Handler for dynamic form field changes
  const handleDynamicFieldChange = (fieldId: string, value: any) => {
    setDynamicFormData(prev => ({ ...prev, [fieldId]: value }))
    // Clear error when user starts typing
    if (dynamicFormErrors[fieldId]) {
      setDynamicFormErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[fieldId]
        return newErrors
      })
    }
  }

  // Validate dynamic form fields
  const validateDynamicForm = (): boolean => {
    if (!templateConfig?.formSchema) return true

    const errors: Record<string, string> = {}

    // Only validate custom sections (exclude Bethesda standard sections)
    templateConfig.formSchema.sections
      .filter(section => section.enabled && !BETHESDA_SECTIONS.includes(section.title))
      .forEach(section => {
        section.fields
          .filter(field => field.enabled && field.required)
          .forEach(field => {
            const fieldKey = `${section.id}_${field.id}`
            const value = dynamicFormData[fieldKey]

            if (value === undefined || value === null || value === '') {
              errors[fieldKey] = `${field.label} là bắt buộc`
            }
          })
      })

    setDynamicFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async () => {
    if (!patientInfo) {
      toast.error('Vui lòng chọn bệnh nhân')
      return
    }

    // Validate dynamic form
    if (!validateDynamicForm()) {
      toast.error('Vui lòng điền đầy đủ các trường bắt buộc')
      return
    }

    setIsSubmitting(true)
    try {
      const payload = {
        patient: patientInfo.id,
        patient_name: formData.patientName,
        age: formData.age,
        gender: formData.gender,
        patient_code: formData.patientCode,
        address: formData.address,
        province: formData.province,
        department: formData.department,
        treating_doctor: formData.treatingDoctor,
        clinical_diagnosis: formData.clinicalDiagnosis,
        specimen_type: formData.specimenType,
        organisms: formData.organisms,
        squamous_cells: formData.squamousCells,
        glandular_cells: formData.glandularCells,
        other_findings: formData.otherFindings,
        conclusion: formData.conclusion,
        recommendation: formData.recommendation,
        cytopathologist: formData.cytopathologist,
        status: 'finalized',  // Auto-finalized - no approval needed
        selected_images: [selectedSnapshot1?.id, selectedSnapshot2?.id].filter(Boolean),  // Only snapshots
        // Note: custom_fields removed - dynamic form data is not stored in database
        // Custom fields from form builder are only for UI layout, not for data storage
      }

      console.log('📋 Cytology Report Payload:', {
        slideId,
        selectedSnapshots: [selectedSnapshot1?.id, selectedSnapshot2?.id].filter(Boolean),
        selected_images: payload.selected_images,
        status: payload.status
      })

      // Use PUT if editing existing report, POST if creating new
      const url = existingReportId
        ? `/api/v1/pathology/cytology-reports/${existingReportId}/`
        : '/api/v1/pathology/cytology-reports/'
      const method = existingReportId ? 'PUT' : 'POST'

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })

      if (response.ok) {
        const data = await response.json()
        if (existingReportId) {
          toast.success(`Đã cập nhật và gửi lại báo cáo #${data.id}`)
        } else {
          toast.success(`Đã tạo báo cáo cytology #${data.id}`)
        }
        onClose()
      } else {
        const errorData = await response.json()
        toast.error(`Lỗi: ${JSON.stringify(errorData)}`)
      }
    } catch (error) {
      console.error('Error saving report:', error)
      toast.error(`Không thể ${existingReportId ? 'cập nhật' : 'tạo'} báo cáo`)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="h-full flex flex-col bg-white border-l-4 border-purple-500 shadow-2xl overflow-hidden">
      {/* Header */}
      <div className="bg-purple-700 text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
        <div>
          <h2 className="text-lg font-bold">🔬 Cytology Report - Bethesda 2014</h2>
          {templateConfig && (
            <p className="text-xs text-purple-100">{templateConfig.hospitalName}</p>
          )}
        </div>
        <button
          onClick={onClose}
          className="text-white hover:text-purple-200 text-xl leading-none font-bold"
        >
          ✕
        </button>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
              <p className="text-sm text-gray-500 mt-3">Đang tải thông tin bệnh nhân...</p>
            </div>
          ) : (
            <>
              {/* Hospital Header */}
              {templateConfig && (
                <div className="text-center mb-6 pb-4 border-b-2 border-gray-300">
                  <h1 className="text-2xl font-bold text-purple-900">{templateConfig.hospitalName}</h1>
                  <h2 className="text-lg font-semibold text-gray-700">{templateConfig.departmentName}</h2>
                  <p className="text-base font-medium text-gray-800 mt-2">{templateConfig.headerText}</p>
                </div>
              )}

              {/* Patient Information */}
              <div className="grid grid-cols-2 gap-4 mb-6 bg-blue-50 p-4 rounded-lg">
                <div>
                  <label className="block text-sm font-medium mb-1">Họ và tên:</label>
                  <input
                    type="text"
                    value={formData.patientName}
                    onChange={(e) => setFormData({ ...formData, patientName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-sm font-medium mb-1">Tuổi:</label>
                    <input
                      type="number"
                      value={formData.age}
                      onChange={(e) => setFormData({ ...formData, age: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Giới tính:</label>
                    <select
                      value={formData.gender}
                      onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="Nữ">Nữ</option>
                      <option value="Nam">Nam</option>
                      <option value="Khác">Khác</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Mã:</label>
                    <input
                      type="text"
                      value={formData.patientCode}
                      onChange={(e) => setFormData({ ...formData, patientCode: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Địa chỉ:</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Tỉnh:</label>
                  <input
                    type="text"
                    value={formData.province}
                    onChange={(e) => setFormData({ ...formData, province: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Khoa phòng:</label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Bác sĩ điều trị:</label>
                  <input
                    type="text"
                    value={formData.treatingDoctor}
                    onChange={(e) => setFormData({ ...formData, treatingDoctor: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Chẩn đoán lâm sàng:</label>
                  <input
                    type="text"
                    value={formData.clinicalDiagnosis}
                    onChange={(e) => setFormData({ ...formData, clinicalDiagnosis: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Loại bệnh phẩm:</label>
                  <select
                    value={formData.specimenType}
                    onChange={(e) => setFormData({ ...formData, specimenType: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="Phết nhúng dịch">Phết nhúng dịch</option>
                    <option value="Phết trực tiếp">Phết trực tiếp</option>
                  </select>
                </div>
              </div>

              {/* Bethesda 2014 Results */}
              <h3 className="text-center text-lg font-bold mb-4 text-purple-900 border-t-2 pt-4">
                KẾT QUẢ TẾ BÀO HỌC CỔ TỬ CUNG THEO BETHESDA 2014 (INTERPRETATION)
              </h3>

              <div className="grid grid-cols-2 gap-6 mb-6">
                {/* Organisms */}
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <h4 className="font-bold text-green-800 mb-3">Biến đổi tế bào do vi sinh vật (Organisms)</h4>
                  <div className="space-y-2">
                    {Object.entries({
                      trichomonas: 'Trichomonas vaginalis',
                      candida: 'Nấm Candida',
                      bacterialVaginosis: 'Thay đổi hệ vi khuẩn âm đạo gợi ý viêm âm đạo do vi khuẩn',
                      actinomyces: 'Actinomyces',
                      hsv: 'Thay đổi tế bào phù hợp với virus herpes simplex (HSV)',
                      hpv: 'Thay đổi tế bào phù hợp với virus u nhú ở người (HPV)'
                    }).map(([key, label]) => (
                      <label key={key} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.organisms[key as keyof typeof formData.organisms]}
                          onChange={(e) => setFormData({
                            ...formData,
                            organisms: { ...formData.organisms, [key]: e.target.checked }
                          })}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Squamous Cells */}
                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <h4 className="font-bold text-red-800 mb-3">Tế bào vảy (Squamous cell)</h4>
                  <div className="space-y-2">
                    {Object.entries({
                      ascus: 'Tế bào vảy không điển hình không rõ ý nghĩa (ASC-US)',
                      asch: 'Tế bào vảy không điển hình không loại trừ tổn thương trong biểu mô độ cao (ASC-H)',
                      lsil: 'Tổn thương trong biểu mô độ thấp (LSIL)',
                      lsilHpv: 'LSIL với hiệu ứng HPV',
                      hsil: 'Tổn thương trong biểu mô độ cao (HSIL)',
                      squamousCarcinoma: 'Ung thư biểu mô vảy'
                    }).map(([key, label]) => (
                      <label key={key} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.squamousCells[key as keyof typeof formData.squamousCells]}
                          onChange={(e) => setFormData({
                            ...formData,
                            squamousCells: { ...formData.squamousCells, [key]: e.target.checked }
                          })}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6 mb-6">
                {/* Other Findings */}
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-bold text-blue-800 mb-3">Biến đổi tế bào khác (Other non neoplastic findings)</h4>
                  <div className="space-y-2">
                    {Object.entries({
                      normalCells: 'Tế bào vảy và tuyến bình thường',
                      inflammation: 'Thay đổi tế bào phản ứng liên quan đến viêm',
                      radiation: 'Thay đổi tế bào phản ứng liên quan đến xạ trị',
                      iud: 'Thay đổi tế bào phản ứng liên quan đến vòng tránh thai (IUD)',
                      atrophy: 'Teo kèm viêm',
                      postHysterectomy: 'Tế bào tuyến sau cắt tử cung',
                      endometrialCells: 'Tế bào nội mạc tử cung ở phụ nữ ≥45 tuổi'
                    }).map(([key, label]) => (
                      <label key={key} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.otherFindings[key as keyof typeof formData.otherFindings]}
                          onChange={(e) => setFormData({
                            ...formData,
                            otherFindings: { ...formData.otherFindings, [key]: e.target.checked }
                          })}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Glandular Cells */}
                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                  <h4 className="font-bold text-yellow-800 mb-3">Tế bào tuyến (Glandular cell)</h4>
                  <div className="space-y-2">
                    {Object.entries({
                      agc: 'Tế bào tuyến không điển hình (AGC)',
                      ais: 'Ung thư tuyến tại chỗ ống cổ tử cung (AIS)',
                      endocervicalAdenocarcinoma: 'Ung thư tuyến ống cổ tử cung',
                      endometrialAdenocarcinoma: 'Ung thư tuyến nội mạc tử cung',
                      adenocarcinomaNos: 'Ung thư tuyến không đặc hiệu (NOS)',
                      adenocarcinomaExtrauterine: 'Ung thư tuyến ngoài tử cung'
                    }).map(([key, label]) => (
                      <label key={key} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.glandularCells[key as keyof typeof formData.glandularCells]}
                          onChange={(e) => setFormData({
                            ...formData,
                            glandularCells: { ...formData.glandularCells, [key]: e.target.checked }
                          })}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Image Selection */}
              <div className="mb-6 border-t-2 pt-4">
                <h4 className="font-bold text-lg mb-3">📸 Chọn hình ảnh cho báo cáo</h4>
                <div className="grid grid-cols-2 gap-4">
                  {/* Image 1 */}
                  <div className="border-2 border-dashed border-purple-300 rounded-lg p-4">
                    <label className="block text-sm font-medium mb-2">Hình ảnh 1:</label>
                    {selectedSnapshot1 ? (
                      <div className="relative">
                        <img
                          src={selectedSnapshot1.image_url}
                          alt="Snapshot 1"
                          className="w-full h-40 object-cover rounded-md mb-2"
                        />
                        <p className="text-xs text-gray-600 mb-2">
                          📸 {selectedSnapshot1.name || `Snapshot ${selectedSnapshot1.id.slice(0, 8)}`}
                        </p>
                        <p className="text-xs text-gray-500">
                          Zoom: {selectedSnapshot1.zoom_level.toFixed(1)}x
                        </p>
                        <button
                          onClick={() => setSelectedSnapshot1(null)}
                          className="text-xs text-red-600 hover:text-red-800 mt-2"
                        >
                          ✕ Xóa
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <select
                          onChange={(e) => {
                            const snapshot = snapshots.find(s => s.id === e.target.value)
                            setSelectedSnapshot1(snapshot || null)
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        >
                          <option value="">-- Chọn snapshot --</option>
                          {snapshots.map(snap => (
                            <option key={snap.id} value={snap.id}>
                              {snap.name || `Snapshot ${snap.id.slice(0, 8)}`} ({snap.zoom_level.toFixed(1)}x)
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>

                  {/* Image 2 */}
                  <div className="border-2 border-dashed border-purple-300 rounded-lg p-4">
                    <label className="block text-sm font-medium mb-2">Hình ảnh 2:</label>
                    {selectedSnapshot2 ? (
                      <div className="relative">
                        <img
                          src={selectedSnapshot2.image_url}
                          alt="Snapshot 2"
                          className="w-full h-40 object-cover rounded-md mb-2"
                        />
                        <p className="text-xs text-gray-600 mb-2">
                          📸 {selectedSnapshot2.name || `Snapshot ${selectedSnapshot2.id.slice(0, 8)}`}
                        </p>
                        <p className="text-xs text-gray-500">
                          Zoom: {selectedSnapshot2.zoom_level.toFixed(1)}x
                        </p>
                        <button
                          onClick={() => setSelectedSnapshot2(null)}
                          className="text-xs text-red-600 hover:text-red-800 mt-2"
                        >
                          ✕ Xóa
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <select
                          onChange={(e) => {
                            const snapshot = snapshots.find(s => s.id === e.target.value)
                            setSelectedSnapshot2(snapshot || null)
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        >
                          <option value="">-- Chọn snapshot --</option>
                          {snapshots.map(snap => (
                            <option key={snap.id} value={snap.id}>
                              {snap.name || `Snapshot ${snap.id.slice(0, 8)}`} ({snap.zoom_level.toFixed(1)}x)
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Conclusion */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Kết luận:</label>
                <textarea
                  value={formData.conclusion}
                  onChange={(e) => setFormData({ ...formData, conclusion: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border-2 border-purple-300 rounded-md"
                />
              </div>

              {/* Recommendation */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Khuyến nghị:</label>
                <textarea
                  value={formData.recommendation}
                  onChange={(e) => setFormData({ ...formData, recommendation: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>

              {/* Cytopathologist */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Bác sĩ đọc kết quả: <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={formData.cytopathologist}
                  onChange={(e) => setFormData({ ...formData, cytopathologist: e.target.value })}
                  placeholder="Nhập tên bác sĩ"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                />
              </div>

              {/* Dynamic Custom Fields */}
              {templateConfig?.formSchema ? (() => {
                // Filter out sections that are already in static form (Bethesda standard sections)
                const customSections = templateConfig.formSchema.sections.filter(
                  s => !BETHESDA_SECTIONS.includes(s.title)
                )

                return customSections.length > 0 ? (
                  <div className="mb-6 border-t-2 pt-6">
                    <h4 className="text-lg font-bold text-purple-900 mb-4">
                      📝 Thông tin bổ sung (Custom Fields)
                      <span className="text-xs text-gray-500 ml-2">
                        ({customSections.filter(s => s.enabled).length} sections)
                      </span>
                    </h4>
                    <DynamicFormRenderer
                      sections={customSections}
                      formData={dynamicFormData}
                      onChange={handleDynamicFieldChange}
                      errors={dynamicFormErrors}
                    />
                  </div>
                ) : null
              })() : null}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t-2">
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting || !patientInfo || !formData.cytopathologist}
                  className="flex-1 bg-purple-600 text-white py-3 px-6 rounded-md hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium text-lg"
                >
                  {isSubmitting ? '⏳ Đang tạo báo cáo...' : '💾 Lưu báo cáo Cytology'}
                </button>
                <button
                  onClick={onClose}
                  className="px-6 py-3 border-2 border-gray-300 rounded-md hover:bg-gray-50 font-medium"
                >
                  Hủy
                </button>
              </div>
            </>
          )}
      </div>
    </div>
  )
}
